<script setup lang="ts">
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { useModal } from '@/composition/use-modal';
  import type { ICopySource } from '@/interfaces/IStoreSetup';

  /**
   * «¿De qué tienda?» (plan §4.3): the owner's other stores with dishes,
   * «{name} · {count} platos». Opened with
   * `showDynamicDialog<number | null>(…, 'v2-menu-copy-picker')`; resolves
   * with the chosen store id, or null when closed.
   */
  defineProps<{ sources: ICopySource[] }>();
  const emit = defineEmits<{ (e: 'close'): void }>();
  const modals = useModal();

  function pick(restaurantId: number) {
    void modals.dialogSubmit('v2-menu-copy-picker', restaurantId);
  }
</script>

<template>
  <div class="v2-menu-copy-picker-modal">
    <header class="modal-head">
      <h2 class="modal-title">{{ $t('menuImport.source.copyPick') }}</h2>
    </header>
    <ul class="source-list">
      <li v-for="source in sources" :key="source.restaurantId">
        <button
          type="button"
          class="source-row"
          @click="pick(source.restaurantId)"
        >
          <span class="source-icon" aria-hidden="true">
            <PosUiIcon name="storefront-outline" />
          </span>
          <span class="source-label">{{
            $tc('menuImport.source.copyCount', source.items, {
              name: source.name,
              count: source.items,
            })
          }}</span>
          <PosUiIcon name="chevron-right" class="source-caret" />
        </button>
      </li>
    </ul>
    <footer class="modal-foot">
      <PosUiButton color="neutral" variant="outline" @click="emit('close')">{{
        $t('cancel')
      }}</PosUiButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-copy-picker-modal {
    @apply tw-flex tw-max-h-[85vh] tw-flex-col tw-bg-white;
    .modal-head {
      @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-4;
    }
    .modal-title {
      @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
    }
    .source-list {
      @apply tw-m-0 tw-flex tw-min-h-0 tw-list-none tw-flex-col tw-gap-2 tw-overflow-y-auto tw-p-4;
    }
    .source-row {
      @apply tw-flex tw-min-h-14 tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-2 tw-text-left tw-transition-colors hover:tw-border-neutral-300 hover:tw-bg-neutral-50;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .source-icon {
      @apply tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-lg tw-text-neutral-700;
    }
    .source-label {
      @apply tw-min-w-0 tw-flex-1 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .source-caret {
      @apply tw-shrink-0 tw-text-xl tw-text-neutral-400;
    }
    .modal-foot {
      @apply tw-flex tw-shrink-0 tw-justify-end tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3;
    }
  }
</style>
